import { find, get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  createBooking,
  updateBooking
} from 'store/v1/bookings/bookings.actions.js';
import { openModal } from 'store/v1/ui/ui.actions.js';
import { getBookingsStatuses } from 'store/v1/statuses/statuses.selectors';
import { createBookingObject } from 'v1/helpers/byModel/BookingHelper';
import { createNewEventObject } from 'v1/helpers/byModel/EventHelper';
import { myServices } from 'v5/services/services.connected.app';

/**
 * What the slot's full search does with a picked resource, as it always has: `booking` is the resource's booking in the production's dates, if any.
 * - assign: the booking editor, on that booking when it has assignments, else on a new one
 *   in the production's dates, in that booking's status;
 * - assignAndConfirm: the booking (or a new one) gets the slot's assignment, confirmed;
 * - assignAndEdit: as assign, a new booking in no status;
 * - assignAsIs: what assign's editor saves untouched, without it (for several at once): the slot
 *   added to the booking with assignments, else a new booking in that booking's status;
 * - statuses / assignWithStatus: a booking status to assign with, as "Assign with status" did:
 *   the slot added to the booking with assignments in that status, else a new booking in it.
 */
export function useSlotAssignActions({ production, slot }) {
  const dispatch = useDispatch();
  const slugAppScope = myServices.appScopeService.useSlugAppScope();
  const bookingTypes = useSelector(state => state.booking_types);
  const statuses = useSelector(state => state.statuses);
  const bookingStatuses = useSelector(getBookingsStatuses);

  const confirmedStatus = () =>
    find(
      get(statuses, 'data'),
      status => !status.archived && status.status_type === 'CONFIRMED'
    );
  const assignmentEvent = () =>
    createNewEventObject({
      ...production.production_date,
      event_type: 'RESOURCE_SLOT_ASSIGNMENT'
    });
  const hasAssignments = booking =>
    Boolean(booking?.resource_slot_assignments?.length);

  const openEditor = (resource, booking, statusId) =>
    dispatch(
      hasAssignments(booking)
        ? openModal(
            'ResourceEventsModal',
            {
              bookingId: booking.id,
              resourceSlotId: slot.id,
              resourceSlotAssignmentEvent: assignmentEvent()
            },
            slugAppScope
          )
        : openModal(
            'ResourceEventsModal',
            {
              resourceId: resource.id,
              resourceSlotId: slot.id,
              event: production.production_date,
              resourceSlotAssignmentEvent: assignmentEvent(),
              statusId
            },
            slugAppScope
          )
    );

  return {
    assign: (resource, booking) =>
      openEditor(resource, booking, booking ? booking.status_id : null),
    assignAndEdit: (resource, booking) => openEditor(resource, booking, null),
    assignAsIs: (resource, booking) => {
      if (hasAssignments(booking)) {
        return dispatch(
          updateBooking(booking.id, {
            ...booking,
            resource_slot_assignments: [
              ...booking.resource_slot_assignments,
              {
                contact_id: resource.id,
                resource_slot_id: slot.id,
                events: [assignmentEvent()]
              }
            ]
          })
        );
      }
      return dispatch(
        createBooking(
          createBookingObject(bookingTypes, {
            event: get(production, 'production_date', null),
            resource,
            slot,
            status: get(statuses, ['data', booking?.status_id], {})
          })
        )
      );
    },
    assignAndConfirm: (resource, booking) => {
      if (hasAssignments(booking)) {
        return dispatch(
          updateBooking(booking.id, {
            ...booking,
            resource_slot_assignments: [
              ...booking.resource_slot_assignments,
              {
                contact_id: resource.id,
                resource_slot_id: slot.id,
                events: [assignmentEvent()]
              }
            ],
            status_id: confirmedStatus().id
          })
        );
      }
      return dispatch(
        createBooking(
          createBookingObject(bookingTypes, {
            event: get(production, 'production_date', null),
            resource,
            slot,
            status: confirmedStatus()
          })
        )
      );
    },
    statuses: bookingStatuses,
    assignWithStatus: (status, resource, booking) => {
      if (hasAssignments(booking)) {
        const alreadyOnSlot = booking.resource_slot_assignments.some(
          assignment =>
            assignment.contact_id === resource.id &&
            assignment.resource_slot_id === slot.id
        );
        return dispatch(
          updateBooking(booking.id, {
            ...booking,
            resource_slot_assignments: alreadyOnSlot
              ? booking.resource_slot_assignments
              : [
                  ...booking.resource_slot_assignments,
                  {
                    contact_id: resource.id,
                    resource_slot_id: slot.id,
                    events: [assignmentEvent()]
                  }
                ],
            status_id: status.id
          })
        );
      }
      return dispatch(
        createBooking(
          createBookingObject(bookingTypes, {
            event: get(production, 'production_date', null),
            resource,
            slot,
            status
          })
        )
      );
    }
  };
}
